import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { useCategories } from '@/context/CategoriesContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Upload, X, ImageIcon, Video, ChevronLeft, ChevronRight, Check, Plus, Trash2, Ruler, ShieldAlert, Truck, Layers, Youtube, Search, Sparkles, Crown, Leaf, Handshake, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { ImageSearchModal } from '@/components/marketplace/ImageSearchModal';
import { GoogleMapsAddressPicker } from '@/components/marketplace/GoogleMapsAddressPicker';
import { AdvancedVariantsEditor } from '@/components/marketplace/AdvancedVariantsEditor';
import { AiListingWizard } from '@/components/marketplace/AiListingWizard';
import { HtmlEditor } from '@/components/common/HtmlEditor';

const uid = () => crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);

// Extract YouTube video ID from any valid URL format
const parseYouTubeId = (url) => {
    if (!url) return null;
    const s = String(url).trim();
    // Direct 11-char id
    if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
    try {
        const u = new URL(s.startsWith('http') ? s : `https://${s}`);
        const host = u.hostname.replace('www.', '');
        if (host === 'youtu.be') {
            const id = u.pathname.slice(1).split('/')[0];
            return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
        }
        if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
            const v = u.searchParams.get('v');
            if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;
            // /embed/<id> or /shorts/<id>
            const parts = u.pathname.split('/').filter(Boolean);
            const idx = parts.findIndex(p => p === 'embed' || p === 'shorts' || p === 'v');
            if (idx >= 0 && parts[idx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[idx + 1])) return parts[idx + 1];
        }
    } catch { /* ignore */ }
    return null;
};

export const youtubeThumb = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

export const AdForm = ({ initial, onSubmit, submitLabel = 'Publicar' }) => {
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const cats = useCategories();
    const [sizeGuides, setSizeGuides] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [ytUrl, setYtUrl] = useState('');
    const [showImgSearch, setShowImgSearch] = useState(false);
    const TOTAL_STEPS = 6;
    const DRAFT_KEY = 'oklovendo_new_ad_draft';

    const DEFAULT_WARRANTY = { enabled: false, months: 6, title: '', terms: '' };
    const DEFAULT_AUCTION = { starting_price: '', reserve_price: '', min_increment: 50, ends_at: '', auto_extend_minutes: 5 };

    const _buildFormState = (src) => {
        const init = src || {};
        return {
            title: '', description: '', price: '', currency: 'MXN',
            category_slug: '', condition: 'usado', location: '',
            shipping_available: false, media: [], tags: [],
            stock: '', variants: [], shipping_methods: [],
            shipping_weight_kg: null, shipping_length_cm: null, shipping_width_cm: null, shipping_height_cm: null,
            size_guide_slug: '',
            moderation_qa: true, moderation_comments: true,
            sale_mode: 'direct', accepts_offers: false,
            ad_type: 'gratuita',
            cashback_percent: 0,
            discount_price: null,
            promo_type: '',
            promo_value: '',
            in_person_pickup_available: false,
            in_person_pickup_instructions: '',
            ...init,
            warranty: (init.warranty && typeof init.warranty === 'object') ? init.warranty : DEFAULT_WARRANTY,
            auction: (init.auction && typeof init.auction === 'object') ? init.auction : DEFAULT_AUCTION,
            variants: Array.isArray(init.variants) ? init.variants : [],
            shipping_methods: Array.isArray(init.shipping_methods) ? init.shipping_methods : [],
            tags: Array.isArray(init.tags) ? init.tags : [],
            media: Array.isArray(init.media) ? init.media : [],
            cashback_percent: Number(init.cashback_percent ?? 0) || 0,
            discount_price: (init.discount_price === '' || init.discount_price == null) ? null : Number(init.discount_price),
        };
    };

    const [form, setForm] = useState(() => {
        // For new ads (no initial), try to restore draft from localStorage
        if (!initial) {
            try {
                const saved = localStorage.getItem(DRAFT_KEY);
                if (saved) return _buildFormState(JSON.parse(saved));
            } catch {}
        }
        return _buildFormState(initial);
    });

    // Persist draft to localStorage on every form change (new ads only)
    useEffect(() => {
        if (!initial) {
            try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch {}
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);
    const [aiOpen, setAiOpen] = useState(false);
    // Ad type pre-step (new ads only)
    const [adTypeStep, setAdTypeStep] = useState(!initial?.ad_type);
    const [planFeats, setPlanFeats] = useState(null);  // plan features object
    const [planLoading, setPlanLoading] = useState(true);
    const [quota, setQuota] = useState(null);

    useEffect(() => {
        api.get('/size-guides').then(r => setSizeGuides(r.data)).catch(() => {});
        api.get('/me/plan')
            .then(r => setPlanFeats(r.data?.plan?.features || r.data?.features || {}))
            .catch(() => setPlanFeats({}))
            .finally(() => setPlanLoading(false));
        api.get('/me/plan/quota').then(r => setQuota(r.data?.usage || null)).catch(() => {});
    }, []);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const upload = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const kind = f.type.startsWith('video') ? 'video' : 'image';
                if (kind === 'image' && form.media.filter(m => m.kind === 'image').length >= 10) { toast.error('Máx 10 fotos'); continue; }
                if (kind === 'video' && form.media.filter(m => m.kind === 'video' || m.kind === 'youtube').length >= 3) { toast.error('Máx 3 videos (incluye YouTube)'); continue; }
                const fd = new FormData();
                fd.append('file', f);
                const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                setForm(prev => ({ ...prev, media: [...prev.media, { id: uid(), kind: data.kind, url: data.url, order: prev.media.length }] }));
            }
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error de subida'); }
        finally { setUploading(false); }
    };

    const addYoutube = () => {
        const id = parseYouTubeId(ytUrl);
        if (!id) { toast.error('URL de YouTube no válida'); return; }
        const videoCount = form.media.filter(m => m.kind === 'video' || m.kind === 'youtube').length;
        if (videoCount >= 3) { toast.error('Máx 3 videos (incluye YouTube)'); return; }
        if (form.media.some(m => m.kind === 'youtube' && m.url === id)) { toast.error('Este video ya fue añadido'); return; }
        setForm(prev => ({ ...prev, media: [...prev.media, { id: uid(), kind: 'youtube', url: id, order: prev.media.length }] }));
        setYtUrl('');
        toast.success('Video de YouTube añadido');
    };

    const removeMedia = (idx) => setForm(f => ({ ...f, media: f.media.filter((_, i) => i !== idx) }));

    // Variants
    const addVariantGroup = (kind) => {
        const defaults = {
            color: { kind: 'color', name: 'Color', options: [{ id: uid(), name: 'Rojo', value: '#dc2626', stock: 5, price_delta: 0, media_ids: [] }] },
            size: { kind: 'size', name: 'Talla', options: [{ id: uid(), name: 'M', value: 'M', stock: 5, price_delta: 0, media_ids: [] }] },
            format: { kind: 'format', name: 'Formato', options: [{ id: uid(), name: 'Estándar', value: 'std', stock: 5, price_delta: 0, media_ids: [] }] },
            material: { kind: 'material', name: 'Material', options: [{ id: uid(), name: 'Algodón', value: 'algodon', stock: 5, price_delta: 0, media_ids: [] }] },
        };
        const base = defaults[kind] || defaults.format;
        setForm(f => ({ ...f, variants: [...f.variants, { id: uid(), ...base }] }));
    };

    const updateVariant = (vIdx, patch) => {
        setForm(f => {
            const variants = [...f.variants];
            variants[vIdx] = { ...variants[vIdx], ...patch };
            return { ...f, variants };
        });
    };
    const removeVariant = (vIdx) => setForm(f => ({ ...f, variants: f.variants.filter((_, i) => i !== vIdx) }));
    const addOption = (vIdx) => {
        const v = form.variants[vIdx];
        const newOpt = { id: uid(), name: 'Opción', value: '', stock: 0, price_delta: 0, media_ids: [] };
        updateVariant(vIdx, { options: [...v.options, newOpt] });
    };
    const updateOption = (vIdx, oIdx, patch) => {
        const v = form.variants[vIdx];
        const options = [...v.options];
        options[oIdx] = { ...options[oIdx], ...patch };
        updateVariant(vIdx, { options });
    };
    const removeOption = (vIdx, oIdx) => {
        const v = form.variants[vIdx];
        updateVariant(vIdx, { options: v.options.filter((_, i) => i !== oIdx) });
    };
    const toggleOptionMedia = (vIdx, oIdx, mediaId) => {
        const opt = form.variants[vIdx].options[oIdx];
        const ids = opt.media_ids || [];
        const next = ids.includes(mediaId) ? ids.filter(i => i !== mediaId) : [...ids, mediaId];
        updateOption(vIdx, oIdx, { media_ids: next });
    };

    // Shipping methods
    const addShipping = () => setForm(f => ({ ...f, shipping_methods: [...f.shipping_methods, { id: uid(), name: 'Estándar', cost: 0, free: true, days_min: 3, days_max: 5, description: '' }] }));
    const updateShipping = (idx, patch) => {
        const list = [...form.shipping_methods];
        list[idx] = { ...list[idx], ...patch };
        set('shipping_methods', list);
    };
    const removeShipping = (idx) => set('shipping_methods', form.shipping_methods.filter((_, i) => i !== idx));

    const validateStep = () => {
        if (step === 1) { if (!form.title.trim() || form.title.trim().length < 3) return 'Ingresa un título (mín 3 letras)'; if (!form.category_slug) return 'Elige una categoría'; }
        if (step === 2) { if (!form.price || Number(form.price) <= 0) return 'Precio inválido'; if (!form.location.trim()) return 'Ubicación requerida'; }
        if (step === 3) { if (form.media.filter(m => m.kind === 'image').length < 1) return 'Sube al menos 1 foto'; }
        if (step === 4) { /* variants optional */ }
        if (step === 5) { /* shipping + warranty optional */ }
        if (step === 6) { const plain = String(form.description || '').replace(/<[^>]*>/g, '').trim(); if (plain.length < 10) return 'Descripción muy corta'; }
        return null;
    };

    const next = () => { const err = validateStep(); if (err) { toast.error(err); return; } setStep(step + 1); };

    const submit = async () => {
        // Prepare auction if applicable
        let auctionPayload = null;
        if (form.sale_mode === 'auction') {
            if (!form.auction.starting_price || !form.auction.ends_at) { toast.error('Configura precio inicial y fin de subasta'); return; }
            const ends = new Date(form.auction.ends_at);
            if (isNaN(ends.getTime()) || ends.getTime() <= Date.now()) { toast.error('El fin de la subasta debe ser futuro'); return; }
            auctionPayload = {
                starting_price: Number(form.auction.starting_price),
                reserve_price: form.auction.reserve_price ? Number(form.auction.reserve_price) : null,
                min_increment: Number(form.auction.min_increment) || 50,
                ends_at: ends.toISOString(),
                auto_extend_minutes: Number(form.auction.auto_extend_minutes) || 5,
            };
        }
        const payload = {
            ...form,
            price: Number(form.price) || (auctionPayload ? Number(form.auction.starting_price) : 0),
            stock: form.stock === '' || form.stock === null ? null : Number(form.stock),
            media: form.media.map((m, i) => ({ ...m, order: i })),
            variants: form.variants,
            variants_v2: form.variants_v2 || null,
            location_data: form.location_data || null,
            shipping_methods: form.shipping_methods.map(s => ({ ...s, cost: Number(s.cost), free: !!s.free })),
            shipping_weight_kg: form.shipping_weight_kg ? Number(form.shipping_weight_kg) : null,
            shipping_length_cm: form.shipping_length_cm ? Number(form.shipping_length_cm) : null,
            shipping_width_cm: form.shipping_width_cm ? Number(form.shipping_width_cm) : null,
            shipping_height_cm: form.shipping_height_cm ? Number(form.shipping_height_cm) : null,
            warranty: form.warranty?.enabled ? { ...form.warranty, months: Number(form.warranty.months) } : null,
            size_guide_slug: form.size_guide_slug || null,
            sale_mode: form.sale_mode || 'direct',
            accepts_offers: !!form.accepts_offers,
            auction: auctionPayload,
            // Sanitize optional numeric / string fields — never send '' to the API
            cashback_percent: Number(form.cashback_percent) || 0,
            discount_price: (form.discount_price === '' || form.discount_price == null) ? null : Number(form.discount_price),
            promo_type: form.promo_type || null,
            promo_value: (form.promo_value === '' || form.promo_value == null) ? null : Number(form.promo_value),
            in_person_pickup_available: !!form.in_person_pickup_available,
            in_person_pickup_instructions: form.in_person_pickup_instructions || null,
        };
        setSaving(true);
        try {
            const r = await onSubmit(payload);
            // Clear draft on successful save
            if (!initial) { try { localStorage.removeItem(DRAFT_KEY); } catch {} }
            toast.success('¡Anuncio guardado!');
            if (r?.id) nav('/dashboard/ads');
        } catch (e) {
            const detail = e?.response?.data?.detail;
            let msg = 'Error al guardar el anuncio';
            if (typeof detail === 'string') msg = detail;
            else if (Array.isArray(detail)) msg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
            else if (e?.message) msg = e.message;
            console.error('[AdForm submit error]', e?.response?.status, detail, e);
            toast.error(msg);
        }
        finally { setSaving(false); }
    };

    // Add selected images from image search
    const addSearchedImages = (urls) => {
        setForm(prev => ({
            ...prev,
            media: [...prev.media, ...urls.map((url, i) => ({ id: uid(), kind: 'image', url, order: prev.media.length + i, external: true }))],
        }));
        toast.success(`${urls.length} imágenes añadidas`);
    };

    // Ad type pre-step — shown before wizard for new ads
    const primeEnabled = !!planFeats?.prime_listing_enabled;
    const gratuitaMax = Number(planFeats?.gratuita_max_per_year || 5);
    const gratuitaRemaining = quota ? quota.gratuita_remaining : (gratuitaMax - (quota?.gratuita_used || 0));
    const primeMonthLimit = planFeats?.prime_listings_per_month === -1 || planFeats?.prime_listings_per_month == null ? -1 : Number(planFeats.prime_listings_per_month);

    if (adTypeStep) {
        if (planLoading) {
            return (
                <div className="space-y-4">
                    <div className="h-6 bg-secondary rounded animate-pulse w-64" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="h-44 bg-secondary rounded-2xl animate-pulse" />
                        <div className="h-44 bg-secondary rounded-2xl animate-pulse" />
                    </div>
                </div>
            );
        }
        return (
            <div className="space-y-6" data-testid="adtype-step">
                <div>
                    <h2 className="text-xl font-semibold tracking-tight">¿Qué tipo de publicación quieres crear?</h2>
                    <p className="text-sm text-muted-foreground mt-1">Elige el tipo antes de continuar con los detalles del anuncio.</p>
                </div>
                <div className={`grid gap-4 ${primeEnabled ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 max-w-sm'}`}>
                    {/* Gratuita */}
                    <button type="button" onClick={() => { set('ad_type', 'gratuita'); setAdTypeStep(false); }}
                        className="text-left rounded-2xl border-2 border-emerald-300 bg-emerald-50/60 hover:border-emerald-500 hover:bg-emerald-50 p-5 transition-all" data-testid="adtype-gratuita">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="h-10 w-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-sm">
                                <Leaf className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-bold text-base text-emerald-900">Publicación Gratuita</div>
                                <div className="text-[11px] text-emerald-700">Sin costo de publicación</div>
                            </div>
                        </div>
                        <ul className="text-sm text-emerald-800 space-y-1">
                            <li className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />0% de comisión al vender</li>
                            <li className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />Duración de {planFeats?.gratuita_listing_duration_days || 90} días</li>
                            <li className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />Hasta {gratuitaMax} al año</li>
                        </ul>
                        {quota && (
                            <div className={`mt-3 text-xs font-medium rounded-lg px-3 py-1.5 ${(quota.gratuita_remaining ?? gratuitaMax) > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}`}>
                                {(quota.gratuita_remaining ?? gratuitaMax) > 0
                                    ? `Te quedan ${quota.gratuita_remaining ?? gratuitaMax} publicaciones gratuitas este año`
                                    : 'Has alcanzado el límite anual de publicaciones gratuitas'}
                            </div>
                        )}
                    </button>

                    {/* Prime (only if plan allows) */}
                    {primeEnabled && (
                        <button type="button" onClick={() => { set('ad_type', 'prime'); setAdTypeStep(false); }}
                            className="text-left rounded-2xl border-2 border-amber-300 bg-amber-50/60 hover:border-amber-500 hover:bg-amber-50 p-5 transition-all" data-testid="adtype-prime">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-sm">
                                    <Crown className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="font-bold text-base text-amber-900">Publicación Prime</div>
                                    <div className="text-[11px] text-amber-700">Mayor visibilidad y alcance</div>
                                </div>
                            </div>
                            <ul className="text-sm text-amber-900 space-y-1">
                                <li className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-amber-600 shrink-0" />Comisión según categoría</li>
                                <li className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-amber-600 shrink-0" />Sin límite de duración</li>
                                <li className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-amber-600 shrink-0" />Posición destacada en búsquedas</li>
                                <li className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-amber-600 shrink-0" />Incluido en tu plan OkGo</li>
                            </ul>
                            {quota && primeMonthLimit !== -1 && (
                                <div className="mt-3 text-xs font-medium rounded-lg px-3 py-1.5 bg-amber-100 text-amber-800">
                                    {quota.prime_used_this_month}/{primeMonthLimit} usadas este mes
                                </div>
                            )}
                            {primeMonthLimit === -1 && (
                                <div className="mt-3 text-xs font-medium rounded-lg px-3 py-1.5 bg-amber-100 text-amber-800">
                                    Ilimitadas incluidas en tu plan
                                </div>
                            )}
                        </button>
                    )}
                </div>
                {!primeEnabled && (
                    <p className="text-xs text-muted-foreground">¿Quieres publicaciones Prime? <span className="text-red-600 font-medium">Actualiza tu plan OkGo</span> para activarlas.</p>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <div className="text-xs text-muted-foreground">Paso {step} de {TOTAL_STEPS}</div>
                <Progress value={(step / TOTAL_STEPS) * 100} className="mt-2" />
            </div>

            <div className="rounded-2xl border bg-card p-6">
                {step === 1 && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Catálogo y título</h2>
                            <Button type="button" size="sm" variant="outline" onClick={() => setAiOpen(true)} data-testid="ai-listing-open"><Sparkles className="h-4 w-4 mr-1 text-red-600" />Publicar con IA</Button>
                        </div>
                        <div><Label>Categoría</Label>
                            <Select value={form.category_slug} onValueChange={(v) => set('category_slug', v)}>
                                <SelectTrigger data-testid="wizard-category"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                                <SelectContent>{(cats || []).map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Título</Label>
                            <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Ej: iPhone 14 Pro 128GB" data-testid="wizard-title" />
                        </div>
                    </div>
                )}
                {step === 2 && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold">Precio, condición y modo de venta</h2>
                        <div>
                            <Label>Modo de venta</Label>
                            <Select value={form.sale_mode} onValueChange={(v) => set('sale_mode', v)}>
                                <SelectTrigger data-testid="wizard-sale-mode"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="direct">Venta directa (precio fijo)</SelectItem>
                                    <SelectItem value="offers">Acepta ofertas / regateo</SelectItem>
                                    <SelectItem value="auction">Subasta</SelectItem>
                                </SelectContent>
                            </Select>
                            {form.sale_mode === 'direct' && (
                                <div className="flex items-center gap-2 mt-2">
                                    <Switch checked={!!form.accepts_offers} onCheckedChange={(v) => set('accepts_offers', !!v)} id="ao" data-testid="wizard-accepts-offers" />
                                    <Label htmlFor="ao" className="text-sm">Además, permitir ofertas del comprador</Label>
                                </div>
                            )}
                        </div>
                        {form.sale_mode === 'auction' && (
                            <div className="grid grid-cols-2 gap-3 border-l-2 border-red-500 pl-3 bg-red-50/40 rounded p-3">
                                <div><Label className="text-xs">Precio inicial</Label><Input type="number" value={form.auction.starting_price} onChange={(e) => set('auction', { ...form.auction, starting_price: e.target.value })} data-testid="auction-starting-price" /></div>
                                <div><Label className="text-xs">Precio de reserva (opcional)</Label><Input type="number" value={form.auction.reserve_price || ''} onChange={(e) => set('auction', { ...form.auction, reserve_price: e.target.value })} /></div>
                                <div><Label className="text-xs">Incremento mínimo</Label><Input type="number" value={form.auction.min_increment} onChange={(e) => set('auction', { ...form.auction, min_increment: e.target.value })} /></div>
                                <div><Label className="text-xs">Auto-extend (min)</Label><Input type="number" value={form.auction.auto_extend_minutes} onChange={(e) => set('auction', { ...form.auction, auto_extend_minutes: e.target.value })} /></div>
                                <div className="col-span-2"><Label className="text-xs">Fin de subasta</Label><Input type="datetime-local" value={form.auction.ends_at || ''} onChange={(e) => set('auction', { ...form.auction, ends_at: e.target.value })} data-testid="auction-ends-at" /></div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>{form.sale_mode === 'auction' ? 'Precio referencia' : 'Precio'} ({form.currency})</Label><Input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} data-testid="wizard-price" /></div>
                            <div><Label>Condición</Label>
                                <Select value={form.condition} onValueChange={(v) => set('condition', v)}>
                                    <SelectTrigger data-testid="wizard-condition"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="nuevo">Nuevo</SelectItem>
                                        <SelectItem value="seminuevo">Seminuevo</SelectItem>
                                        <SelectItem value="usado">Usado</SelectItem>
                                        <SelectItem value="para_partes">Para partes</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <GoogleMapsAddressPicker
                                value={form.location_data || (form.location ? { address: form.location } : {})}
                                onChange={(loc) => setForm(f => ({ ...f, location_data: loc, location: loc?.address || '' }))}
                                testId="wizard-location"
                            />
                        </div>
                        <div><Label>Stock base (déjalo vacío si tiene variantes con stock)</Label>
                            <Input type="number" min="0" value={form.stock} onChange={(e) => set('stock', e.target.value)} placeholder="Ej: 12" data-testid="wizard-stock" />
                        </div>

                        {/* Promotions & Benefits block */}
                        <div className="rounded-xl border p-4 space-y-4 bg-gradient-to-br from-amber-50/50 to-emerald-50/50">
                            <h3 className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" />Ofertas y beneficios al comprador</h3>

                            {/* Cashback */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm">Cashback (saldo OkWallet al comprador)</Label>
                                    <Switch checked={form.cashback_percent > 0} onCheckedChange={(v) => set('cashback_percent', v ? 5 : 0)} data-testid="cashback-toggle" />
                                </div>
                                {form.cashback_percent > 0 && (
                                    <div className="flex items-center gap-2">
                                        <Input type="number" min="1" max="30" step="0.5" value={form.cashback_percent} onChange={(e) => set('cashback_percent', parseFloat(e.target.value) || 0)} className="w-24" data-testid="cashback-percent" />
                                        <span className="text-sm text-muted-foreground">% del precio como cashback</span>
                                    </div>
                                )}
                            </div>

                            {/* Discount price */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm">Precio de oferta (tachado en el anuncio)</Label>
                                    <Switch checked={form.discount_price != null && form.discount_price !== ''} onCheckedChange={(v) => set('discount_price', v ? (Number(form.price) || 0) : null)} data-testid="discount-toggle" />
                                </div>
                                {form.discount_price != null && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">Precio original</span>
                                        <Input type="number" min="0" value={form.discount_price} onChange={(e) => set('discount_price', e.target.value)} className="w-32" placeholder="Precio anterior" data-testid="discount-price" />
                                        <span className="text-xs text-muted-foreground">MXN (se mostrará tachado)</span>
                                    </div>
                                )}
                            </div>

                            {/* Promo type */}
                            <div className="space-y-2">
                                <Label className="text-sm">Promoción especial</Label>
                                <Select value={form.promo_type || '__none__'} onValueChange={(v) => set('promo_type', v === '__none__' ? '' : v)}>
                                    <SelectTrigger data-testid="promo-type"><SelectValue placeholder="Sin promoción" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">Sin promoción</SelectItem>
                                        <SelectItem value="2x1">2×1 — Lleva 2, paga 1</SelectItem>
                                        <SelectItem value="3x2">3×2 — Lleva 3, paga 2</SelectItem>
                                        <SelectItem value="buy1_get2_discounted">2do artículo con descuento</SelectItem>
                                    </SelectContent>
                                </Select>
                                {form.promo_type === 'buy1_get2_discounted' && (
                                    <div className="flex items-center gap-2">
                                        <Input type="number" min="1" max="99" value={form.promo_value || ''} onChange={(e) => set('promo_value', e.target.value)} className="w-24" placeholder="%" data-testid="promo-value" />
                                        <span className="text-xs text-muted-foreground">% de descuento en el 2do artículo</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {step === 3 && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold">Fotos y videos</h2>
                        <p className="text-xs text-muted-foreground">Máx 10 fotos + 3 videos (subidos o de YouTube). La primera foto será la portada.</p>
                        <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer hover:bg-secondary transition-colors">
                            <Upload className="h-6 w-6 text-muted-foreground" />
                            <span className="mt-2 text-sm">{uploading ? 'Subiendo…' : 'Selecciona o arrastra archivos'}</span>
                            <input type="file" multiple hidden accept="image/*,video/*" onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="wizard-upload" />
                        </label>

                        {/* Native camera capture (mobile) */}
                        <label className="flex items-center justify-center gap-2 rounded-xl border p-3 cursor-pointer bg-red-50 hover:bg-red-100 transition-colors">
                            <span className="text-sm font-medium text-red-700">📷 Tomar foto con cámara</span>
                            <input type="file" hidden accept="image/*" capture="environment" onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="wizard-camera-capture" />
                        </label>

                        {/* AI Listing Generator */}
                        {form.media.filter(m => m.kind === 'image').length > 0 && (
                            <div className="rounded-xl border p-3 bg-gradient-to-br from-red-50 to-amber-50">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="h-4 w-4 text-red-600" />
                                        <Label className="text-sm font-medium">Generar anuncio con IA (título, descripción, categoría, precio)</Label>
                                    </div>
                                    <Button type="button" size="sm" onClick={async () => {
                                        const firstImg = form.media.find(m => m.kind === 'image');
                                        const url = firstImg.url.startsWith('http') ? firstImg.url : `${(process.env.REACT_APP_BACKEND_URL || '')}${firstImg.url}`;
                                        try {
                                            const { data } = await api.post('/ai/listing-from-image', { image_url: url });
                                            setForm(f => ({ ...f, title: data.title || f.title, description: data.description || f.description, category_slug: data.category_slug || f.category_slug, condition: data.condition || f.condition, tags: data.tags || f.tags, price: data.suggested_price_mxn || f.price }));
                                            toast.success(data._fallback ? 'Modo fallback' : 'Anuncio generado con IA');
                                        } catch (e) { toast.error('Error IA'); }
                                    }} className="bg-red-600 hover:bg-red-700" data-testid="ai-generate-listing">
                                        <Sparkles className="h-3 w-3 mr-1" />Generar
                                    </Button>
                                </div>
                                <p className="text-[11px] text-muted-foreground">Usa la primera foto para generar todo el anuncio automáticamente con visión IA.</p>
                            </div>
                        )}

                        {/* Image search with AI */}
                        <div className="rounded-xl border p-3 bg-neutral-50">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Search className="h-4 w-4 text-red-600" />
                                    <Label className="text-sm font-medium">Buscar imágenes profesionales con IA</Label>
                                </div>
                                <Button type="button" size="sm" onClick={() => setShowImgSearch(true)} className="bg-red-600 hover:bg-red-700" data-testid="open-image-search">
                                    <Search className="h-3 w-3 mr-1" />Abrir buscador
                                </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground">Explora Unsplash y Pexels. La IA sugiere palabras clave a partir del título del anuncio.</p>
                        </div>

                        {/* YouTube URL input */}
                        <div className="rounded-xl border p-3 bg-neutral-50">
                            <div className="flex items-center gap-2 mb-2">
                                <Youtube className="h-4 w-4 text-red-600" />
                                <Label className="text-sm font-medium">Añadir video de YouTube por URL</Label>
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    value={ytUrl}
                                    onChange={(e) => setYtUrl(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addYoutube(); } }}
                                    placeholder="https://www.youtube.com/watch?v=... o https://youtu.be/..."
                                    className="flex-1 h-9"
                                    data-testid="wizard-youtube-url"
                                />
                                <Button type="button" onClick={addYoutube} size="sm" className="bg-red-600 hover:bg-red-700" data-testid="wizard-youtube-add">
                                    <Plus className="h-3 w-3 mr-1" />Añadir
                                </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">Acepta enlaces normales, youtu.be, /embed/ o /shorts/.</p>
                        </div>

                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {form.media.map((m, i) => (
                                <div key={m.id || i} className="relative aspect-square rounded-lg overflow-hidden border bg-neutral-100" data-testid={`media-thumb-${i}`}>
                                    {m.kind === 'youtube' ? (
                                        <img src={youtubeThumb(m.url)} alt="youtube" className="h-full w-full object-cover" />
                                    ) : m.kind === 'video' ? (
                                        <video src={mediaUrl(m.url)} muted className="h-full w-full object-cover" />
                                    ) : (
                                        <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" />
                                    )}
                                    <button onClick={() => removeMedia(i)} type="button" className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/95 flex items-center justify-center shadow" data-testid={`media-remove-${i}`}><X className="h-3 w-3" /></button>
                                    <div className="absolute bottom-1 left-1 bg-black/60 rounded px-1 flex items-center gap-1">
                                        {m.kind === 'youtube' ? <Youtube className="h-3 w-3 text-white" /> : m.kind === 'video' ? <Video className="h-3 w-3 text-white" /> : <ImageIcon className="h-3 w-3 text-white" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {step === 4 && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2"><Layers className="h-5 w-5 text-red-600" />Variantes avanzadas (opcional)</h2>
                        <p className="text-xs text-muted-foreground">Define atributos (Talla, Color, etc.) con sus opciones. Se generará automáticamente una matriz con precio diferenciado, stock y foto por combinación.</p>
                        <AdvancedVariantsEditor
                            value={form.variants_v2 || { axes: [], combos: [] }}
                            onChange={(next) => setForm(f => ({ ...f, variants_v2: next }))}
                            categoryKey={form.category_slug}
                        />

                        {['ropa', 'muebles'].includes(form.category_slug) || (form.variants_v2?.axes || []).some(a => a.type === 'size') ? (
                            <div className="rounded-xl border p-3">
                                <div className="flex items-center gap-2 mb-2"><Ruler className="h-4 w-4 text-red-600" /><Label className="text-sm">Guía de tallas (opcional)</Label></div>
                                <Select value={form.size_guide_slug || 'none'} onValueChange={(v) => set('size_guide_slug', v === 'none' ? '' : v)}>
                                    <SelectTrigger data-testid="wizard-size-guide"><SelectValue placeholder="Ninguna" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Ninguna</SelectItem>
                                        {sizeGuides.map(g => <SelectItem key={g.slug} value={g.slug}>{g.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">Los compradores verán esta tabla al comprar.</p>
                            </div>
                        ) : null}
                    </div>
                )}
                {step === 5 && (
                    <div className="space-y-6">
                        {/* Shipping */}
                        <div>
                            <h2 className="text-lg font-semibold flex items-center gap-2"><Truck className="h-5 w-5 text-red-600" />Métodos de envío</h2>
                            <p className="text-xs text-muted-foreground">Añade opciones de envío. Puedes ofrecer envío gratis o con costo.</p>
                            <div className="flex items-center gap-3 mt-2 mb-3">
                                <Switch checked={form.shipping_available} onCheckedChange={(v) => set('shipping_available', v)} data-testid="wizard-shipping-available" />
                                <Label className="cursor-pointer">Envío disponible</Label>
                            </div>
                            {form.shipping_available && (
                                <div className="space-y-2">
                                    {form.shipping_methods.map((s, i) => (
                                        <div key={s.id} className="rounded-lg border p-3 grid grid-cols-12 gap-2 items-center" data-testid={`shipping-method-${i}`}>
                                            <Input placeholder="Nombre" className="col-span-3 h-8" value={s.name} onChange={(e) => updateShipping(i, { name: e.target.value })} />
                                            <Input placeholder="Descripción" className="col-span-3 h-8" value={s.description} onChange={(e) => updateShipping(i, { description: e.target.value })} />
                                            <Input type="number" placeholder="Min días" className="col-span-1 h-8" value={s.days_min || ''} onChange={(e) => updateShipping(i, { days_min: Number(e.target.value) })} />
                                            <Input type="number" placeholder="Max días" className="col-span-1 h-8" value={s.days_max || ''} onChange={(e) => updateShipping(i, { days_max: Number(e.target.value) })} />
                                            <Input type="number" placeholder="Costo" className="col-span-2 h-8" disabled={s.free} value={s.cost} onChange={(e) => updateShipping(i, { cost: Number(e.target.value) })} />
                                            <label className="col-span-1 inline-flex items-center gap-1 text-xs">
                                                <input type="checkbox" checked={s.free} onChange={(e) => updateShipping(i, { free: e.target.checked, cost: e.target.checked ? 0 : s.cost })} />Gratis
                                            </label>
                                            <Button type="button" size="sm" variant="ghost" className="col-span-1" onClick={() => removeShipping(i)}><X className="h-3 w-3" /></Button>
                                        </div>
                                    ))}
                                    <Button type="button" variant="outline" size="sm" onClick={addShipping} data-testid="add-shipping"><Plus className="h-3 w-3 mr-1" />Añadir método</Button>
                                </div>
                            )}
                            {/* Package dimensions for real shipping (Phase 20f) */}
                            {form.shipping_available && (
                                <div className="mt-3 pt-3 border-t">
                                    <div className="text-xs font-medium mb-2 text-muted-foreground uppercase">Peso y dimensiones del paquete</div>
                                    <p className="text-[11px] text-muted-foreground mb-2">Se usan para cotizar envíos reales con OkEnvíos y comparativa entre vendedores.</p>
                                    <div className="grid grid-cols-4 gap-2">
                                        <div>
                                            <Label className="text-xs">Peso (kg)</Label>
                                            <Input type="number" min="0.1" step="0.1" value={form.shipping_weight_kg || ''} onChange={(e) => set('shipping_weight_kg', parseFloat(e.target.value) || null)} placeholder="1.0" data-testid="ship-weight" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Largo (cm)</Label>
                                            <Input type="number" min="1" value={form.shipping_length_cm || ''} onChange={(e) => set('shipping_length_cm', parseFloat(e.target.value) || null)} placeholder="20" data-testid="ship-length" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Ancho (cm)</Label>
                                            <Input type="number" min="1" value={form.shipping_width_cm || ''} onChange={(e) => set('shipping_width_cm', parseFloat(e.target.value) || null)} placeholder="15" data-testid="ship-width" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Alto (cm)</Label>
                                            <Input type="number" min="1" value={form.shipping_height_cm || ''} onChange={(e) => set('shipping_height_cm', parseFloat(e.target.value) || null)} placeholder="10" data-testid="ship-height" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* In-person pickup */}
                        <div className="rounded-xl border p-3 space-y-3">
                            <div className="flex items-center gap-3">
                                <Handshake className="h-4 w-4 text-emerald-600" />
                                <Label className="text-sm font-medium cursor-pointer">Entrega en persona disponible</Label>
                                <Switch checked={!!form.in_person_pickup_available} onCheckedChange={(v) => set('in_person_pickup_available', v)} data-testid="inperson-toggle" />
                            </div>
                            {form.in_person_pickup_available && (
                                <div className="space-y-2">
                                    <Label className="text-xs">Instrucciones de entrega</Label>
                                    <Textarea rows={3} value={form.in_person_pickup_instructions || ''} onChange={(e) => set('in_person_pickup_instructions', e.target.value)} placeholder="Ej: Disponible para entrega en Plaza Fiesta Americana, Guadalajara. Horarios: L-V 10am-7pm, Sáb 10am-3pm. Se puede acordar otro punto de encuentro concurrido." data-testid="inperson-instructions" />
                                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800 space-y-0.5">
                                        <div className="font-semibold">⚠️ Consejos de seguridad para el comprador</div>
                                        <div>• Elige siempre lugares públicos y concurridos (centros comerciales, bancos, tiendas)</div>
                                        <div>• Nunca acuerdes entregas en domicilios o zonas alejadas</div>
                                        <div>• El pago queda retenido en OkWallet hasta que el comprador lo libere</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Warranty */}
                        <div className="rounded-xl border p-3">
                            <div className="flex items-center gap-3 mb-3">
                                <ShieldAlert className="h-4 w-4 text-red-600" />
                                <Label className="cursor-pointer" htmlFor="warranty-switch">Ofrezco garantía en este producto</Label>
                                <Switch id="warranty-switch" checked={form.warranty.enabled} onCheckedChange={(v) => set('warranty', { ...form.warranty, enabled: v })} data-testid="warranty-toggle" />
                            </div>
                            {form.warranty.enabled && (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-3 gap-2">
                                        <div><Label className="text-xs">Meses</Label>
                                            <Select value={String(form.warranty.months)} onValueChange={(v) => set('warranty', { ...form.warranty, months: Number(v) })}>
                                                <SelectTrigger data-testid="warranty-months"><SelectValue /></SelectTrigger>
                                                <SelectContent>{[1, 3, 6, 12, 18, 24, 36, 60].map(m => <SelectItem key={m} value={String(m)}>{m} meses</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="col-span-2"><Label className="text-xs">Título</Label><Input value={form.warranty.title || ''} onChange={(e) => set('warranty', { ...form.warranty, title: e.target.value })} placeholder="Ej: Garantía 6 meses vs defectos de fábrica" data-testid="warranty-title" /></div>
                                    </div>
                                    <div><Label className="text-xs">Términos y condiciones</Label>
                                        <Textarea rows={4} value={form.warranty.terms || ''} onChange={(e) => set('warranty', { ...form.warranty, terms: e.target.value })} placeholder="Explica qué cubre y qué no, cómo hacer válida la garantía, exclusiones..." data-testid="warranty-terms" />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Moderation */}
                        <div className="rounded-xl border p-3 space-y-2">
                            <Label className="text-sm font-medium">Moderación</Label>
                            <div className="flex items-center gap-3">
                                <Switch checked={form.moderation_qa} onCheckedChange={(v) => set('moderation_qa', v)} data-testid="moderation-qa" />
                                <Label className="cursor-pointer text-sm">Revisar preguntas antes de publicarlas</Label>
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch checked={form.moderation_comments} onCheckedChange={(v) => set('moderation_comments', v)} data-testid="moderation-comments" />
                                <Label className="cursor-pointer text-sm">Revisar comentarios antes de publicarlos</Label>
                            </div>
                            <p className="text-xs text-muted-foreground">Podrás aprobar o rechazar cada pregunta / comentario desde el panel.</p>
                        </div>
                    </div>
                )}
                {step === 6 && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold">Descripción y revisión</h2>
                        <div><Label>Descripción</Label>
                            <HtmlEditor value={form.description} onChange={(v) => set('description', v)} rows={8} placeholder="Describe con detalle: características, estado, incluidos, entrega..." dataTestId="wizard-description" />
                        </div>
                        <div><Label>Etiquetas (separadas por coma)</Label>
                            <Input value={(form.tags || []).join(', ')} onChange={(e) => set('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))} placeholder="Ej: apple, celular, 128gb" />
                        </div>
                        <div className="rounded-lg bg-neutral-50 border p-3 text-sm space-y-1">
                            <div><span className="text-muted-foreground">Título:</span> {form.title}</div>
                            <div><span className="text-muted-foreground">Categoría:</span> {form.category_slug}</div>
                            <div><span className="text-muted-foreground">Precio:</span> ${form.price} {form.currency}</div>
                            <div><span className="text-muted-foreground">Fotos/Videos:</span> {form.media.filter(m => m.kind === 'image').length} + {form.media.filter(m => m.kind === 'video' || m.kind === 'youtube').length}</div>
                            <div><span className="text-muted-foreground">Variantes:</span> {form.variants.length} grupos</div>
                            <div><span className="text-muted-foreground">Métodos de envío:</span> {form.shipping_methods.length}</div>
                            <div><span className="text-muted-foreground">Garantía:</span> {form.warranty?.enabled ? `Sí (${form.warranty.months} meses)` : 'No'}</div>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex justify-between">
                {step > 1 ? <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wizard-back"><ChevronLeft className="h-4 w-4 mr-1" />Atrás</Button> : <span />}
                {step < TOTAL_STEPS ? <Button onClick={next} className="bg-red-600 hover:bg-red-700" data-testid="wizard-next">Siguiente<ChevronRight className="h-4 w-4 ml-1" /></Button> :
                    <Button onClick={submit} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="wizard-submit">{saving ? 'Guardando…' : submitLabel}<Check className="h-4 w-4 ml-1" /></Button>}
            </div>

            <ImageSearchModal
                open={showImgSearch}
                onOpenChange={setShowImgSearch}
                adTitle={form.title}
                adCategory={form.category_slug}
                onPick={addSearchedImages}
            />
            <AiListingWizard
                open={aiOpen}
                onOpenChange={setAiOpen}
                onFilled={(payload) => {
                    setForm(prev => ({
                        ...prev,
                        title: payload.title || prev.title,
                        description: payload.description || prev.description,
                        category_slug: payload.category_slug || prev.category_slug,
                        condition: payload.condition || prev.condition,
                        tags: payload.tags?.length ? payload.tags : prev.tags,
                        price: payload.suggested_price || prev.price,
                        media: payload.image_url ? [{ id: uid(), kind: 'image', url: payload.image_url, order: prev.media.length }, ...prev.media] : prev.media,
                    }));
                    toast.success('Formulario prellenado por IA. Revisa y ajusta.');
                }}
            />
        </div>
    );
};

export default AdForm;
